import { ModifierKeys } from "@/enums/keyboardShortcuts";

import { isMacOs } from "./normalizeShortcut";

import type { UserKeyboardShortcut } from "@/models/options";

// ---------------------------------------------------------------------------
// Reserved shortcut sets — OS and browser shortcuts that must not be
// overridden by the application. Order within each set does not matter;
// they are merged into a single Set for O(1) conflict checking.
// ---------------------------------------------------------------------------

const macOsShortcuts = new Set([
  "⌘ + Space",
  "⌘ + Tab",
  "⌘ + Q",
  "⌘ + W",
  "⌘ + H",
  "⌘ + M",
  "⌘ + Shift + 3",
  "⌘ + Shift + 4",
  "⌘ + Shift + 5",
  "CTRL + ARROWUP",
  "CTRL + ARROWDOWN",
  "⌘ + CTRL + Q",
]);

const winOsShortcuts = new Set([
  "META + D",
  "META + E",
  "META + L",
  "META + R",
  "META + Tab",
  "ALT + Tab",
  "ALT + F4",
  "CTRL + ALT + Delete",
  "META + PrintScreen",
]);

const macBrowserShortcuts = new Set([
  "⌘ + T",
  "⌘ + N",
  "SHIFT + ⌘ + N",
  "⌘ + R",
  "⌘ + J",
  "SHIFT + ⌘ + R",
  "⌘ + F",
  "⌘ + P",
  "⌘ + S",
  "⌘ + O",
  "⌘ + Y",
  "SHIFT + ⌘ + J",
  "SHIFT + ⌘ + Delete",
  "⌘ + Plus",
  "⌘ + Minus",
  "⌘ + 0",
]);

const winBrowserShortcuts = new Set([
  "CTRL + T",
  "CTRL + W",
  "CTRL + N",
  "CTRL + SHIFT + N",
  "CTRL + Tab",
  "CTRL + SHIFT + Tab",
  "CTRL + R",
  "CTRL + SHIFT + R",
  "CTRL + F",
  "CTRL + P",
  "CTRL + S",
  "CTRL + O",
  "CTRL + H",
  "CTRL + J",
  "CTRL + SHIFT + Delete",
  "CTRL + Plus",
  "CTRL + Minus",
  "CTRL + 0",
  "F5",
  "F11",
  "F12",
]);

const reservedCombos = isMacOs
  ? new Set([...macOsShortcuts, ...macBrowserShortcuts])
  : new Set([...winOsShortcuts, ...winBrowserShortcuts]);

// Maps macOS Option key character outputs back to their physical key equivalents.
// When Option is held on macOS, the OS produces special Unicode characters instead
// of the actual letter (e.g. Option+S → "ß"). This map reverses that so we can
// display and store the intended key.
const macOptionCharacterReplacementMap: Record<string, string> = {
  å: "A",
  "∫": "B",
  ç: "C",
  "∂": "D",
  "´": "E", // Dead key
  ƒ: "F",
  "©": "G",
  "˙": "H",
  ˆ: "I", // Dead key
  "∆": "J",
  "˚": "K",
  "¬": "L",
  µ: "M",
  "˜": "N", // Dead key
  ø: "O",
  π: "P",
  œ: "Q",
  "®": "R",
  ß: "S",
  "†": "T",
  "¨": "U", // Dead key
  "√": "V",
  "∑": "W",
  "≈": "X",
  "¥": "Y",
  Ω: "Z",
  "¡": "1",
  "™": "2",
  "£": "3",
  "¢": "4",
  "∞": "5",
  "§": "6",
  "¶": "7",
  "•": "8",
  ª: "9",
  º: "0",
  "–": "-",
  "≠": "=",
  "≤": ",",
  "≥": ".",
  "÷": "/",
  '"': "[",
  "'": "]",
  "«": "\\",
  æ: "'",
  "…": ";",
  Î: "D",
  ω: "Z",
  Â: "M",
};

// Maps keyboard event modifier flags to their display label and
// underscore-joined action_string part (used for API/storage).
const MODIFIER_DEFS = [
  { eventKey: "ctrlKey", display: "CTRL", actionPart: "ctrl" },
  {
    eventKey: "altKey",
    display: isMacOs ? "OPTION" : "ALT",
    actionPart: "alt",
  },
  { eventKey: "shiftKey", display: "SHIFT", actionPart: "shift" },
  {
    eventKey: "metaKey",
    display: isMacOs ? "⌘" : "META",
    actionPart: isMacOs ? "cmd" : "ctrl",
  },
] as const;

export const useCustomizeKeyboardShortcuts = () => {
  const checkForConflict = (shortcut: string) => reservedCombos.has(shortcut);

  const captureKeyboardShortcut = (evt: KeyboardEvent) => {
    const DEAD_KEY = "Dead";

    const activeModifiers = MODIFIER_DEFS.filter(
      (modifier) => evt[modifier.eventKey as keyof KeyboardEvent]
    );
    const modifiers: string[] = activeModifiers.map(
      (modifier) => modifier.display
    );
    const actionStringParts: string[] = activeModifiers.map(
      (modifier) => modifier.actionPart
    );

    const key = evt.key;

    const shortcutParts = [...modifiers];
    let normalizedKey = key.toUpperCase();
    if (key === " ") {
      normalizedKey = "Space";
    }
    if (key.startsWith("Arrow")) {
      normalizedKey = key.replace("Arrow", "").toUpperCase();
    }
    if (key === DEAD_KEY && evt.code.indexOf("Key") > -1) {
      normalizedKey = evt.code.split("Key")[1];
    }

    // replace `OPTION + *` mapped key combo with keyboard key equivalent for display to users
    if (isMacOs && evt.altKey && macOptionCharacterReplacementMap[key]) {
      normalizedKey = macOptionCharacterReplacementMap[key];
    }

    shortcutParts.push(normalizedKey);
    const shortcut = shortcutParts.join(" + ");

    if (key === DEAD_KEY) {
      // we cannot recognize a 'dead' keypress in mac os
      return {
        hasError: true,
        action: shortcut,
      };
    }

    if (
      ["Control", "Alt", "Shift", "Meta", "Command", "Option"].includes(key)
    ) {
      return {
        pending: true,
        modifiers,
      };
    }

    // Normalize key for the underscore-joined action_string format
    let normalizedActionKey = key.toLowerCase();
    if (key === " ") {
      normalizedActionKey = "space";
    } else if (key.startsWith("Arrow")) {
      normalizedActionKey = key.replace("Arrow", "").toLowerCase();
    } else if (key === "\\") {
      normalizedActionKey = "backslash";
    } else if (key === "/") {
      normalizedActionKey = "slash";
    } else if (key.length === 1) {
      normalizedActionKey = key.toLowerCase();
    }

    actionStringParts.push(normalizedActionKey);
    const actionString = actionStringParts.join("_");

    const hasConflict = checkForConflict(shortcut);

    return {
      hasConflict: hasConflict,
      isValid: modifiers.length > 0,
      action: shortcut,
      action_string: actionString,
      keys: [key],
      modifiers,
    };
  };

  const mapModifiers = (modifiers: string[]): ModifierKeys[] => {
    return modifiers
      .map((modifier) => {
        if (modifier === "CTRL") {
          return ModifierKeys.Ctrl;
        }
        if (modifier === "ALT" || modifier === "OPTION") {
          return ModifierKeys.Alt;
        }
        if (modifier === "SHIFT") {
          return ModifierKeys.Shift;
        }
        if (modifier === "⌘" || modifier === "META") {
          return ModifierKeys.Meta;
        }
        return undefined;
      })
      .filter((item): item is ModifierKeys => item !== undefined);
  };

  const mapConfiguredShortcuts = (shortcuts: UserKeyboardShortcut[]) => {
    return shortcuts.reduce<Record<string, Omit<UserKeyboardShortcut, "key">>>(
      (result, sc) => {
        result[sc.key] = {
          action: sc.action,
          action_name: sc.action_name,
          action_string: sc.action_string,
          context: sc.context,
          is_custom: sc.is_custom,
          keys: sc.keys,
          modifiers: sc.modifiers,
        };
        return result;
      },
      {}
    );
  };

  return {
    checkForConflict,
    captureKeyboardShortcut,
    mapModifiers,
    mapConfiguredShortcuts,
  };
};
